/*
 * Copyright The OpenTelemetry Authors
 * SPDX-License-Identifier: Apache-2.0
 */

import type { DiagLogger, DiagLogFunction } from './types';

type ConsoleMapKeys = 'error' | 'warn' | 'info' | 'debug' | 'trace';
const consoleMap: { n: keyof DiagLogger; c: ConsoleMapKeys }[] = [
  { n: 'error', c: 'error' },
  { n: 'warn', c: 'warn' },
  { n: 'info', c: 'info' },
  { n: 'debug', c: 'debug' },
  { n: 'verbose', c: 'trace' },
];

// Save original console methods at module load time, before any instrumentation
// can wrap them. This ensures DiagConsoleLogger calls the unwrapped originals.
// Exported for testing only — not part of the public API.
export const _originalConsoleMethods: Partial<
  Record<ConsoleMapKeys | 'log', typeof console.log>
> = {};
if (typeof console !== 'undefined') {
  const keys: (ConsoleMapKeys | 'log')[] = [
    'error',
    'warn',
    'info',
    'debug',
    'trace',
    'log',
  ];
  for (const key of keys) {
    // eslint-disable-next-line no-console
    if (typeof console[key] === 'function') {
      // eslint-disable-next-line no-console
      _originalConsoleMethods[key] = console[key];
    }
  }
}

/**
 * A simple Immutable Console based diagnostic logger which will output any messages to the Console.
 * If you want to limit the amount of logging to a specific level or lower use the
 * {@link createLogLevelDiagLogger}
 *
 * @since 1.0.0
 */
export class DiagConsoleLogger implements DiagLogger {
  constructor() {
    function _consoleFunc(funcName: ConsoleMapKeys): DiagLogFunction {
      return function (...args) {
        // Prefer original (pre-instrumentation) methods saved at module load time.
        let theFunc = _originalConsoleMethods[funcName];
        // Some environments only expose the console when the F12 developer console is open
        if (typeof theFunc !== 'function') {
          theFunc = _originalConsoleMethods['log'];
        }
        // Fall back in case console was not available at module load time but became available later.
        if (typeof theFunc !== 'function' && console) {
          // eslint-disable-next-line no-console
          theFunc = console[funcName];
          if (typeof theFunc !== 'function') {
            // eslint-disable-next-line no-console
            theFunc = console.log;
          }
        }
        if (typeof theFunc === 'function') {
          return theFunc.apply(console, args);
        }
      };
    }

    for (let i = 0; i < consoleMap.length; i++) {
      this[consoleMap[i].n] = _consoleFunc(consoleMap[i].c);
    }
  }

  /** Log an error scenario that was not expected and caused the requested operation to fail. */
  public error!: DiagLogFunction;

  /**
   * Log a warning scenario to inform the developer of an issues that should be investigated.
   * The requested operation may or may not have succeeded or completed.
   */
  public warn!: DiagLogFunction;

  /**
   * Log a general informational message, this should not affect functionality.
   * This is also the default logging level so this should NOT be used for logging
   * debugging level information.
   */
  public info!: DiagLogFunction;

  /**
   * Log a general debug message that can be useful for identifying a failure.
   * Information logged at this level may include diagnostic details that would
   * help identify a failure scenario. Useful scenarios would be to log the execution
   * order of async operations
   */
  public debug!: DiagLogFunction;

  /**
   * Log a detailed (verbose) trace level logging that can be used to identify failures
   * where debug level logging would be insufficient, this level of tracing can include
   * input and output parameters and as such may include PII information passing through
   * the API. As such it is recommended that this level of tracing should not be enabled
   * in a production environment.
   */
  public verbose!: DiagLogFunction;
}



// WEBPACK FOOTER //
// ../../../src/diag/consoleLogger.ts